<template>
  <div v-if="loading" class="flex justify-center py-20">
    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
  </div>

  <div v-else-if="error" class="text-center py-20">
    <p class="text-red-500">{{ error }}</p>
  </div>

  <div v-else>
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-xl font-bold text-gray-900 mb-1">{{ business.name }}</h1>
      <p class="text-sm text-gray-500">{{ campaign.name }} &mdash; {{ posts.length }} posts for review</p>
    </div>

    <!-- Progress bar -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
      <div class="flex justify-between text-sm mb-2">
        <span class="text-gray-600">Approval Progress</span>
        <span class="font-medium text-gray-900">{{ approvedCount }} / {{ posts.length }} approved</span>
      </div>
      <div class="w-full bg-gray-200 rounded-full h-2.5">
        <div class="bg-green-500 rounded-full h-2.5 transition-all" :style="{ width: progressPercent + '%' }" />
      </div>
    </div>

    <!-- Author name (persistent) -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
      <label class="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
      <input v-model="authorName" type="text"
        class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        :placeholder="clientName || 'Enter your name'" />
    </div>

    <!-- Posts -->
    <div class="space-y-6">
      <div v-for="post in posts" :key="post.id"
        class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">

        <!-- Post status badge -->
        <div :class="postBannerClass(post)" class="px-4 py-2 text-xs font-medium flex justify-between items-center">
          <span>{{ post.keyword }}</span>
          <span>{{ postStatusLabel(post) }}</span>
        </div>

        <!-- Post image -->
        <div v-if="post.image_url">
          <img :src="post.image_url" class="w-full" :alt="post.keyword" />
        </div>

        <!-- Post content -->
        <div class="px-4 py-3">
          <h3 v-if="post.title" class="font-semibold text-gray-900 text-sm mb-1">{{ post.title }}</h3>
          <p class="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed line-clamp-4"
            :class="{ '!line-clamp-none': expandedPosts[post.id] }">
            {{ post.body }}
          </p>
          <button v-if="post.body && post.body.length > 200" @click="expandedPosts[post.id] = !expandedPosts[post.id]"
            class="text-xs text-primary-500 hover:text-primary-600 mt-1">
            {{ expandedPosts[post.id] ? 'Show less' : 'Read more' }}
          </button>
        </div>

        <!-- CTA preview -->
        <div v-if="post.cta_type" class="px-4 pb-3">
          <span class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-full">
            {{ post.cta_type === 'call_now' ? 'Call now' : 'Learn more' }}
          </span>
        </div>

        <!-- Action buttons -->
        <div v-if="canActOnPost(post)" class="px-4 pb-3 flex gap-2">
          <button @click="handleApprove(post)" :disabled="submittingPost === post.id"
            class="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-medium py-2 px-3 rounded-lg text-sm transition-colors">
            {{ submittingPost === post.id ? 'Submitting...' : 'Approve' }}
          </button>
          <button @click="toggleChangesForm(post.id)" :disabled="submittingPost === post.id"
            class="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-medium py-2 px-3 rounded-lg text-sm transition-colors">
            Request Changes
          </button>
        </div>

        <!-- Request changes form -->
        <div v-if="changesFormPost === post.id && canActOnPost(post)" class="px-4 pb-3">
          <textarea v-model="changesComment" rows="2"
            class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 mb-2"
            placeholder="Describe the changes you'd like..."></textarea>
          <div class="flex justify-end">
            <button @click="handleRequestChanges(post)" :disabled="!changesComment.trim() || submittingPost === post.id"
              class="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-medium py-1.5 px-4 rounded-lg text-sm transition-colors">
              Submit
            </button>
          </div>
        </div>

        <!-- Comments -->
        <div v-if="post.comments && post.comments.length" class="border-t border-gray-100">
          <div class="px-4 py-2 space-y-2">
            <div v-for="comment in post.comments" :key="comment.id"
              :class="comment.author_type === 'client' ? 'bg-amber-50' : 'bg-gray-50'"
              class="rounded-lg px-3 py-2">
              <div class="flex justify-between items-start mb-0.5">
                <span class="text-xs font-medium" :class="comment.author_type === 'client' ? 'text-amber-700' : 'text-primary-700'">
                  {{ comment.author_name }}
                </span>
                <span class="text-[10px] text-gray-400">{{ formatTime(comment.created_at) }}</span>
              </div>
              <p class="text-xs text-gray-700">{{ comment.body }}</p>
            </div>
          </div>

          <!-- Add comment -->
          <div v-if="post.approval_status !== 'approved'" class="px-4 pb-3">
            <div class="flex gap-2">
              <input v-model="postComments[post.id]" type="text"
                class="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:ring-2 focus:ring-primary-500"
                placeholder="Add a comment..." @keyup.enter="handleAddComment(post)" />
              <button @click="handleAddComment(post)" :disabled="!postComments[post.id]?.trim()"
                class="bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white font-medium py-1.5 px-3 rounded-lg text-xs transition-colors">
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, reactive, onMounted } from 'vue'
import { getCampaignReviewData, approveReviewPost, requestReviewPostChanges, addReviewPostComment } from '~/javascript/api/campaign_reviews'

const props = defineProps({ token: { type: String, required: true } })

const loading = ref(true)
const error = ref(null)
const submittingPost = ref(null)
const changesFormPost = ref(null)
const changesComment = ref('')
const authorName = ref('')
const expandedPosts = reactive({})
const postComments = reactive({})

const campaign = ref({})
const business = ref({})
const clientName = ref('')
const posts = ref([])

const approvedCount = computed(() => posts.value.filter((p) => p.approval_status === 'approved').length)
const progressPercent = computed(() => {
  if (!posts.value.length) return 0
  return Math.round((approvedCount.value / posts.value.length) * 100)
})

function effectiveAuthor() {
  return authorName.value || clientName.value || 'Client'
}

function canActOnPost(post) {
  return ['pending', 'revised'].includes(post.approval_status)
}

function postStatusLabel(post) {
  const labels = { pending: 'Awaiting review', approved: 'Approved', changes_requested: 'Changes requested', revised: 'Revised' }
  return labels[post.approval_status] || ''
}

function postBannerClass(post) {
  const classes = {
    pending: 'bg-blue-50 text-blue-800',
    approved: 'bg-green-50 text-green-800',
    changes_requested: 'bg-amber-50 text-amber-800',
    revised: 'bg-blue-50 text-blue-800'
  }
  return classes[post.approval_status] || 'bg-gray-50 text-gray-800'
}

function toggleChangesForm(postId) {
  changesFormPost.value = changesFormPost.value === postId ? null : postId
  changesComment.value = ''
}

function formatTime(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
}

async function fetchData() {
  try {
    const { data } = await getCampaignReviewData(props.token)
    campaign.value = data.campaign
    business.value = data.business
    clientName.value = data.client_name || ''
    posts.value = data.posts
  } catch (e) {
    error.value = 'Unable to load review. The link may have expired.'
  } finally {
    loading.value = false
  }
}

async function handleApprove(post) {
  submittingPost.value = post.id
  try {
    await approveReviewPost(props.token, post.id, effectiveAuthor())
    post.approval_status = 'approved'
    post.approved_at = new Date().toISOString()
    post.comments.push({ id: Date.now(), body: 'Approved', author_type: 'client', author_name: effectiveAuthor(), created_at: new Date().toISOString() })
  } catch (e) {
    error.value = 'Failed to approve. Please try again.'
  } finally {
    submittingPost.value = null
  }
}

async function handleRequestChanges(post) {
  submittingPost.value = post.id
  try {
    const { data } = await requestReviewPostChanges(props.token, post.id, changesComment.value, effectiveAuthor())
    post.approval_status = 'changes_requested'
    post.comments = data.comments
    changesComment.value = ''
    changesFormPost.value = null
  } catch (e) {
    error.value = 'Failed to submit. Please try again.'
  } finally {
    submittingPost.value = null
  }
}

async function handleAddComment(post) {
  const body = postComments[post.id]?.trim()
  if (!body) return
  try {
    const { data } = await addReviewPostComment(props.token, post.id, body, effectiveAuthor())
    post.comments.push(data)
    postComments[post.id] = ''
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to add comment:', e)
  }
}

onMounted(fetchData)
</script>
